/*
  Sticky add to cart bar (mobile)
  - Shows when the product form's buttons block has been scrolled past
  - Hides again from the Related Products section and downwards (incl. footer)
  - The bar's button proxies a click on the real add to cart button, so all
    button states (preorder, sold out, notify me, members only, market
    restrictions, collab links) keep working without duplicated logic.
  - For products with options the bar's button always says "select size" and
    opens a light drawer — the add itself happens from the drawer, so the
    chosen size stays visible and changeable right up to the add. The drawer
    holds no variant state of its own: picking a size checks the matching
    option input in the product form and everything else (bar, drawer, PDP)
    re-reads its state from that form.
*/
(function () {
  'use strict';

  if (customElements.get('product-sticky-bar')) return;

  const selectors = {
    optionSelector: '[data-single-option-selector]',
    optionPosition: '[data-option-position]',
    mainOption: 'input[name^="options"], [data-popout-option]',
  };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const moneyFormat = format || '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    let value = '';
    switch (moneyFormat.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('[data-section-type="product"]');
      if (!this.container) return;

      // The buttons block is where the real add to cart button lives, but it is
      // also what the scroll trigger measures. Keeping those the same element
      // meant a product whose form is laid out differently disabled the bar
      // outright — silently, since the markup still renders. Only the button
      // lookup depends on the block now; the trigger falls back to whatever
      // marker the product form does render.
      this.buttonsBlock = this.container.querySelector('.product__block--buttons');
      this.formAnchor =
        this.buttonsBlock ||
        this.container.querySelector('product-form') ||
        this.container.querySelector('[data-product-variants]') ||
        this.container.querySelector('[data-product]');
      this.barButton = this.querySelector('[data-sticky-atc-button]');
      this.barButtonText = this.querySelector('[data-sticky-atc-button-text]');
      this.variantLabel = this.querySelector('[data-sticky-atc-variant]');
      this.priceLabel = this.querySelector('[data-sticky-atc-price]');
      this.compareLabel = this.querySelector('[data-sticky-atc-compare]');
      this.infoButton = this.querySelector('[data-sticky-atc-scroll]');

      if (!this.formAnchor || !this.barButton) return;

      this.selectSizeText = this.getAttribute('data-select-size-text') || '';
      // Not data-add-to-cart-text — the theme's product form JS blanks the
      // innerHTML of every [data-add-to-cart-text] element in the section.
      this.addToCartText = this.getAttribute('data-sticky-atc-add-text') || '';
      // Without the section's own option selectors there is nothing to write a
      // size back to, so fall back to plain "proxy the main button" behavior.
      this.hasOptions =
        this.hasAttribute('data-has-options') &&
        Boolean(this.container.querySelector(selectors.optionPosition));
      this.hasSelection = !this.hasOptions || this.hasAttribute('data-selected');

      // Same move the theme makes for the quick add dialog: a transformed or
      // clipping ancestor would otherwise break the dialog's fixed position.
      this.drawer = this.container.querySelector('[data-sticky-atc-drawer]');
      if (this.drawer) document.body.appendChild(this.drawer);

      this.drawerButton = this.drawer
        ? this.drawer.querySelector('[data-sticky-atc-drawer-button]')
        : null;
      this.drawerButtonText = this.drawer
        ? this.drawer.querySelector('[data-sticky-atc-drawer-button-text]')
        : null;
      this.drawerPrice = this.drawer
        ? this.drawer.querySelector('[data-sticky-atc-drawer-price]')
        : null;
      this.drawerCompare = this.drawer
        ? this.drawer.querySelector('[data-sticky-atc-drawer-compare]')
        : null;
      this.drawerIsAnimating = false;

      this.loadingTimeout = null;

      this.onProductAdded = () => {
        this.resetLoading();
        this.drawerClose();
      };
      this.onProductAddError = () => {
        this.resetLoading();
        this.drawerClose();
        this.scrollToForm();
      };
      this.onVariantChange = (event) => this.variantChange(event);
      this.onOptionChange = () => this.markSelected();

      document.addEventListener('theme:product:added', this.onProductAdded);
      document.addEventListener('theme:product:add-error', this.onProductAddError);
      this.container.addEventListener('theme:variant:change', this.onVariantChange);

      // The popout select dispatches a non-bubbling `change`, so listen on the
      // option inputs themselves rather than delegating from the container.
      this.optionSelectors = Array.from(
        this.container.querySelectorAll(selectors.optionSelector)
      );
      this.optionSelectors.forEach((input) => {
        input.addEventListener('change', this.onOptionChange);
      });

      // After an add the theme re-enables its button from a one second
      // timeout — later than theme:product:added, the bar's last sync. Without
      // watching the button, a product without options kept a disabled bar
      // button until the page reloaded.
      this.mainButtonObserver = new MutationObserver(() => {
        if (this.isLoading()) return;
        this.syncFromMain();
      });
      const mainButton = this.getMainButton();
      if (mainButton) {
        this.mainButtonObserver.observe(mainButton, {
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      this.barButton.addEventListener('click', (event) => {
        event.preventDefault();

        if (this.isSelectSizeMode()) {
          this.drawerOpen();
          return;
        }

        this.proxyMainButton(this.barButton);
      });

      if (this.infoButton) {
        this.infoButton.addEventListener('click', () => this.scrollToForm());
      }

      this.drawerEvents();

      // Hide the bar once the footer comes into view.
      this.stopElement =
        document.querySelector('.site-footer-wrapper') ||
        document.querySelector('footer');

      // A passive scroll listener with a rAF tick instead of
      // IntersectionObserver — Safari throttles observer callbacks during
      // momentum scrolling, which made the bar react visibly late on iOS.
      this.visible = false;
      this.tickScheduled = false;
      this.onScroll = () => {
        if (this.tickScheduled) return;
        this.tickScheduled = true;
        requestAnimationFrame(() => {
          this.tickScheduled = false;
          this.update();
        });
      };

      // The drawer belongs to the bar — don't leave it open past the width
      // where product-sticky-bar.css hides the bar itself.
      this.onResize = () => {
        if (window.innerWidth > 990) this.drawerClose();
        this.onScroll();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.syncFromMain();
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product:added', this.onProductAdded);
      document.removeEventListener('theme:product:add-error', this.onProductAddError);
      if (this.container) {
        this.container.removeEventListener('theme:variant:change', this.onVariantChange);
      }
      if (this.optionSelectors) {
        this.optionSelectors.forEach((input) => {
          input.removeEventListener('change', this.onOptionChange);
        });
      }
      if (this.mainButtonObserver) this.mainButtonObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      // The drawer lives on <body>, so it doesn't get removed with the section.
      if (this.drawer) this.drawer.remove();
      document.body.classList.remove('sticky-atc-visible');
    }

    getMainButton() {
      if (this.buttonsBlock) {
        return this.buttonsBlock.querySelector(
          'button[data-add-to-cart], [data-open-notify-me-modal], a.btn, button.btn'
        );
      }

      // Without the block to scope to, ask for the buttons that matter by name
      // one at a time — a combined selector returns whichever comes first in
      // the document, which section-wide would be some unrelated .btn.
      return (
        this.container.querySelector('button[data-add-to-cart]') ||
        this.container.querySelector('[data-open-notify-me-modal]')
      );
    }

    /* Products with options always route through the drawer — even with a
       size picked, so reselecting never requires scrolling back to the form. */
    isSelectSizeMode() {
      return (
        this.hasOptions &&
        Boolean(this.drawer) &&
        Boolean(this.getMainButton())
      );
    }

    proxyMainButton(button) {
      const mainButton = this.getMainButton();
      if (!mainButton || mainButton.hasAttribute('disabled')) return;

      const isAddToCart = mainButton.hasAttribute('data-add-to-cart');

      // Anything that isn't an add to cart opens its own UI (notify me modal,
      // account page, collab link) — get the drawer out of the way first.
      if (!isAddToCart) {
        this.drawerClose();
      } else {
        this.setLoading(button, true);
      }

      mainButton.click();
    }

    update() {
      // Show once the buy buttons are fully scrolled out above the viewport.
      const buttonsRect = this.formAnchor.getBoundingClientRect();
      const pastForm = buttonsRect.bottom < 0;

      // Hide again when the footer reaches the bar at the bottom of the
      // viewport.
      let inTail = false;
      if (this.stopElement) {
        inTail =
          this.stopElement.getBoundingClientRect().top <
          window.innerHeight - this.offsetHeight;
      }

      const visible = pastForm && !inTail;
      if (visible === this.visible) return;

      this.visible = visible;
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sticky-atc-visible', visible);
    }

    /* A real option input changed, so the shopper has now picked a variant. */
    markSelected() {
      if (this.hasSelection) return;
      this.hasSelection = true;
      this.setAttribute('data-selected', '');
      this.syncFromMain();
    }

    variantChange(event) {
      const variant = event.detail ? event.detail.variant : null;
      const moneyFormat = window.theme && window.theme.moneyFormat;

      if (variant) {
        if (this.variantLabel) {
          this.variantLabel.textContent = variant.title;
          this.variantLabel.hidden = !this.hasSelection;
        }

        const free = window.theme && window.theme.strings && window.theme.strings.free;
        const price =
          variant.price === 0 && free
            ? free
            : formatMoney(variant.price, moneyFormat);
        const onSale = variant.compare_at_price > variant.price;
        const comparePrice = onSale
          ? formatMoney(variant.compare_at_price, moneyFormat)
          : '';

        [
          [this.priceLabel, this.compareLabel],
          [this.drawerPrice, this.drawerCompare],
        ].forEach(([priceElement, compareElement]) => {
          if (priceElement) {
            priceElement.textContent = price;
          }

          if (compareElement) {
            compareElement.hidden = !onSale;
            compareElement.textContent = comparePrice;
          }
        });
      }

      // The theme updates the main button's text/disabled state on the same
      // event — wait a frame so we copy the updated state.
      requestAnimationFrame(() => this.syncFromMain());
    }

    syncFromMain() {
      const mainButton = this.getMainButton();

      if (this.variantLabel) {
        this.variantLabel.hidden = !this.hasSelection;
      }

      this.syncDrawerOptions();

      if (!mainButton) return;

      const textElement = mainButton.querySelector('.btn__text') || mainButton;
      const text = textElement.textContent.trim();

      if (this.isSelectSizeMode()) {
        this.barButtonText.textContent = this.selectSizeText;
        this.barButton.removeAttribute('disabled');
        this.barButton.classList.remove('sold-out-button');
      } else {
        // Mirror the form's button one to one — including the sold-out grey,
        // which the form keeps even on its notify me trigger.
        if (text) this.barButtonText.textContent = text;
        this.barButton.toggleAttribute(
          'disabled',
          mainButton.hasAttribute('disabled')
        );
        this.barButton.classList.toggle(
          'sold-out-button',
          mainButton.classList.contains('sold-out-button')
        );
      }

      if (this.drawerButton) {
        if (this.hasSelection) {
          if (text && this.drawerButtonText) {
            this.drawerButtonText.textContent = text;
          }
          this.drawerButton.toggleAttribute(
            'disabled',
            mainButton.hasAttribute('disabled')
          );
          this.drawerButton.classList.toggle(
            'sold-out-button',
            mainButton.classList.contains('sold-out-button')
          );
        } else {
          if (this.drawerButtonText) {
            this.drawerButtonText.textContent = this.addToCartText;
          }
          this.drawerButton.setAttribute('disabled', '');
        }
      }
    }

    /* Mirror checked + sold out/unavailable state from the product form. */
    syncDrawerOptions() {
      if (!this.drawer) return;

      this.drawer
        .querySelectorAll('[data-sticky-atc-option-input]')
        .forEach((input) => {
          const fieldset = input.closest('[data-sticky-atc-option]');
          if (!fieldset) return;

          const mainOption = this.getMainOption(
            fieldset.getAttribute('data-sticky-atc-option'),
            input.value
          );
          if (!mainOption) return;

          input.checked = this.hasSelection && this.isChecked(mainOption);
          input.classList.toggle(
            'sold-out',
            mainOption.classList.contains('sold-out')
          );
          input.classList.toggle(
            'unavailable',
            mainOption.classList.contains('unavailable')
          );
        });
    }

    isChecked(mainOption) {
      if (mainOption.tagName === 'INPUT') return mainOption.checked;

      const scope = mainOption.closest(selectors.optionPosition);
      const popoutInput = scope && scope.querySelector('[data-popout-input]');
      return (
        Boolean(popoutInput) &&
        popoutInput.value === mainOption.getAttribute('data-value')
      );
    }

    /*
      The radio input (or popout option link) in the product form that stands
      for this option value. Matching on the element's own value keeps quoted
      and escaped option names working.
    */
    getMainOption(position, value) {
      const root =
        this.container.querySelector('[data-product-variants]') || this.container;
      const scope = root.querySelector(`[data-option-position="${position}"]`);
      if (!scope) return null;

      const options = scope.querySelectorAll(selectors.mainOption);
      for (let index = 0; index < options.length; index++) {
        const option = options[index];
        const optionValue = option.value || option.getAttribute('data-value');
        if (optionValue === value) return option;
      }

      return null;
    }

    /* Single source of truth: write the choice back into the product form. */
    selectOption(position, value) {
      const mainOption = this.getMainOption(position, value);
      if (!mainOption) return;

      if (mainOption.tagName === 'INPUT') {
        if (mainOption.checked) return;
        mainOption.checked = true;
        mainOption.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Let the theme's popout handle its own input, label and change event.
        mainOption.click();
      }
    }

    drawerEvents() {
      if (!this.drawer) return;

      this.drawer.addEventListener('change', (event) => {
        const input = event.target.closest('[data-sticky-atc-option-input]');
        if (!input) return;

        const fieldset = input.closest('[data-sticky-atc-option]');
        if (!fieldset) return;

        // Write to the product form first — markSelected() re-reads the drawer
        // from it, so flipping the flag any earlier would blank the choice.
        this.selectOption(
          fieldset.getAttribute('data-sticky-atc-option'),
          input.value
        );
        this.markSelected();
      });

      this.drawer
        .querySelector('[data-sticky-atc-drawer-close]')
        ?.addEventListener('click', (event) => {
          event.preventDefault();
          this.drawerClose();
        });

      this.drawer.addEventListener('click', (event) => {
        if (event.target === this.drawer) this.drawerClose();
      });

      this.drawer.addEventListener('keydown', (event) => {
        if (event.code === 'Escape') {
          event.preventDefault();
          this.drawerClose();
        }
      });

      // The dialog fades out with an animation — close it once that's done.
      this.drawer.addEventListener('animationend', (event) => {
        if (event.target !== this.drawer) return;
        this.drawerIsAnimating = false;

        if (this.drawer.hasAttribute('closing')) this.drawerClose();
      });

      if (this.drawerButton) {
        this.drawerButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.proxyMainButton(this.drawerButton);
        });
      }
    }

    drawerOpen() {
      if (!this.drawer || this.drawer.hasAttribute('open')) return;

      this.syncFromMain();

      if (typeof this.drawer.show === 'function') this.drawer.show();
      this.drawer.setAttribute('open', 'true');
      this.drawer.removeAttribute('inert');

      // Same staggered content fade as the quick add: the sheet slides up and
      // the blocks fade in one after another (theme.css drawer-items-fade).
      requestAnimationFrame(() => {
        this.drawer.querySelectorAll('[data-animation]').forEach((item) => {
          item.classList.add('is-animated');
        });
      });

      document.dispatchEvent(
        new CustomEvent('theme:scroll:lock', { bubbles: true })
      );

      const focusTarget = this.drawer.querySelector(
        '[data-sticky-atc-drawer-close]'
      );
      if (focusTarget) focusTarget.focus();
    }

    drawerClose() {
      if (!this.drawer || !this.drawer.hasAttribute('open')) return;
      if (this.drawerIsAnimating) return;

      // First call starts the fade out, the animationend handler calls back.
      if (!this.drawer.hasAttribute('closing')) {
        this.drawer.setAttribute('closing', '');
        this.drawerIsAnimating = true;
        return;
      }

      if (typeof this.drawer.close === 'function') {
        this.drawer.close();
      } else {
        this.drawer.removeAttribute('open');
      }

      this.drawer.removeAttribute('closing');
      this.drawer.setAttribute('inert', '');

      // Reset the content fade so reopening replays it (theme's quick add
      // does the same in its resetAnimatedItems()).
      this.drawer.querySelectorAll('[data-animation]').forEach((item) => {
        item.classList.remove('is-animated');
        item.classList.remove('is-hiding');
      });

      const hasOpenDrawer = document.querySelector('[data-cart-drawer].is-open');
      const hasOpenModal = document.querySelector('dialog[open]');

      if (!hasOpenDrawer && !hasOpenModal) {
        document.dispatchEvent(
          new CustomEvent('theme:scroll:unlock', { bubbles: true })
        );
      }
    }

    setLoading(button, isLoading) {
      if (!button) return;

      button.classList.toggle('is-loading', isLoading);
      button.toggleAttribute('disabled', isLoading);

      if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
      if (isLoading) {
        this.loadingTimeout = setTimeout(() => this.resetLoading(), 8000);
      }
    }

    isLoading() {
      return [this.barButton, this.drawerButton].some(
        (button) => button && button.classList.contains('is-loading')
      );
    }

    /* Drop the spinner and hand the disabled state back to the product form. */
    resetLoading() {
      this.setLoading(this.barButton, false);
      this.setLoading(this.drawerButton, false);
      this.syncFromMain();
    }

    scrollToForm() {
      const target =
        this.container.querySelector('[data-product-variants]') ||
        this.formAnchor;
      const header = document.querySelector('[data-header-height]');
      const offset = (header ? header.offsetHeight : 80) + 20;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
})();
